import React, { useEffect } from "react";
import { useDispatch, useSelector } from "react-redux";
import { useParams, Link } from "react-router-dom";
import { Container, Message, Icon } from "semantic-ui-react";
import { getProfileById } from "../../actions/profileActions";
import { getPatternsByUserId } from "../../actions/patternActions";
import { profileSelector, patternListSelector, authSelector } from "../../utils/selectors";
import isEmpty from "../../validation/is-empty";
import Spinner from "../../components/Spinner";
import AboutDesigner from "../Profiles/AboutDesigner";
import PrimaryButton from "../../components/Button/PrimaryButton";
import { FormattedMessage } from "react-intl";
import styles from "./Profile.module.scss";
import { paramsSchema } from "../../schemas/globalSchema";
import api from "../../utils/api";

const Profile: React.FC<any> = () => {
  const dispatch = useDispatch();
  const { id } = useParams();
  const { profile, loading } = useSelector((state) => profileSelector(state));
  const { patterns, loading: patternsLoading } = useSelector((state: any) => patternListSelector(state));
  const { user } = useSelector((state: any) => authSelector(state));

  const isOwnProfile = user && id === user._id;

  useEffect(() => {
  if (!id) return;

  const run = async () => {
    let finalId: string | undefined;

    // check if id is valid ObjectId
    const result = paramsSchema.safeParse({ id });

    if (result.success) {
      finalId = result.data.id;
    } else {
      // fallback: treat as profile handle
      try {
        const res = await api.get(`profile/handle/${id}`);

        finalId = res.data?.user?._id;
      } catch (error: any) {
        console.error("Failed to fetch profile by handle:", error?.response?.msg);

        return;
      }
    }

    //safety check before dispatch
    if (!finalId) return;

    dispatch(getProfileById(finalId));
    dispatch(getPatternsByUserId(finalId));
  };

  run();
}, [id, dispatch]);

  if (loading || patternsLoading) {
    return <Spinner />;
  }

  const profileWithPatterns = { ...profile, products: patterns || [] };

  return !isEmpty(profile) ? (
    <div className={styles.profilePage}>
      {isOwnProfile && (
        <div className={styles.editBanner}>
          <Container>
            <Link to="/manage-profile" className={styles.editLink}>
              <Icon name="edit" />
              <FormattedMessage id="profile.editProfile" defaultMessage="Edit profile" />
            </Link>
          </Container>
        </div>
      )}
      <AboutDesigner profile={profileWithPatterns} />
    </div>
  ) : (
    <Container className={styles.noProfileContainer}>
      <Message icon info={isOwnProfile} warning={!isOwnProfile} size="large">
        <Icon name={isOwnProfile ? "user circle" : "exclamation triangle"} />
        <Message.Content>
          <Message.Header>
            {isOwnProfile ? (
              <FormattedMessage id="profile.noProfile.ownTitle" defaultMessage="No Profile Yet" />
            ) : (
              <FormattedMessage id="profile.noProfile.otherTitle" defaultMessage="Profile Not Found" />
            )}
          </Message.Header>
          <p>
            {isOwnProfile ? (
              <FormattedMessage
                id="profile.noProfile.ownMessage"
                defaultMessage="You haven't created your designer profile yet. Set up your profile to showcase your work and connect with customers!"
              />
            ) : (
              <FormattedMessage
                id="profile.noProfile.otherMessage"
                defaultMessage="This user hasn't set up their profile yet."
              />
            )}
          </p>
          {isOwnProfile && (
            <Link to="/manage-profile" className={styles.createProfileLink}>
              <PrimaryButton text={
                <FormattedMessage id="profile.noProfile.button" defaultMessage="Create Your Profile" />
              } />
            </Link>
          )}
        </Message.Content>
      </Message>
    </Container>
  );
};

export default Profile;
